import type { ReactNode } from 'react';

/**
 * A circular progress indicator, drawn in SVG so it stays crisp at any density.
 *
 * Progress is shown as a ring rather than a bar because it reads at a glance in a corner of the
 * header without taking a full row of width — the same reason activity rings work on a watch.
 */
export function ProgressRing({
	value,
	size = 56,
	stroke = 5,
	children,
}: {
	/** 0-1. Values outside the range are clamped rather than drawn wrong. */
	value: number;
	size?: number;
	stroke?: number;
	children?: ReactNode;
}) {
	const clamped = Math.min(1, Math.max(0, value));
	const radius = (size - stroke) / 2;
	const circumference = 2 * Math.PI * radius;

	return (
		<div className="relative shrink-0" style={{ width: size, height: size }}>
			<svg width={size} height={size} className="-rotate-90" role="img" aria-label={`${Math.round(clamped * 100)}% complete`}>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					stroke="var(--brand-line)"
					strokeWidth={stroke}
				/>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					stroke="var(--brand-primary)"
					strokeWidth={stroke}
					strokeLinecap="round"
					strokeDasharray={circumference}
					strokeDashoffset={circumference * (1 - clamped)}
					style={{ transition: 'stroke-dashoffset 420ms cubic-bezier(0.2, 0.8, 0.2, 1)' }}
				/>
			</svg>
			{children && (
				<div className="absolute inset-0 flex items-center justify-center">{children}</div>
			)}
		</div>
	);
}
